<template>
  <div class="hierarchical-management-page">
    <!-- Main Top Card Header -->
    <CCard class="staff-ui-card bg-white mb-4">
      <CCardHeader class="staff-ui-header d-flex flex-wrap justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <div class="header-main-icon mr-3 d-flex align-items-center justify-content-center">
            <CIcon name="cil-sitemap" size="xl" />
          </div>
          <div>
            <h4 class="card-title mb-0 text-white font-weight-bold">
              {{ $t('jobList.pageTitle') }}
            </h4>
            <div class="header-sub-text mt-1 d-flex align-items-center">
              <CIcon name="cil-layers" class="mr-1 opacity-75" size="sm" />
              <span>{{ $t('jobList.pageSubtitle') }}</span>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center gap-1 mt-2 mt-md-0">
          <button type="button" class="btn-top-add-section" @click="openAddSectionModal">
            <span>{{ $t('jobList.addSection') }}</span>
          </button>
        </div>
      </CCardHeader>

      <CCardBody class="staff-ui-body p-3 p-md-4 bg-light">
        <!-- Control Bar: Search & Global Actions -->
        <div class="control-bar-surface d-flex flex-wrap justify-content-between align-items-center gap-3 bg-white p-3 border mb-4 shadow-sm">
          <!-- Search Input -->
          <div class="d-flex align-items-center flex-grow-1" style="max-width: 480px;">
            <div class="input-group search-input-group">
              <input
                v-model.trim="searchKeyword"
                type="text"
                class="form-control"
                :placeholder="$t('jobList.searchPlaceholder') || 'ค้นหาชื่อส่วนงาน, รหัส, ชื่อฝ่าย, หรือชื่องาน...'"
              />
              <div class="input-group-append" v-if="searchKeyword">
                <button class="btn btn-outline-secondary border-left-0" type="button" @click="searchKeyword = ''">✕</button>
              </div>
            </div>
          </div>

          <!-- Quick Stats & Expand/Collapse Toggle -->
          <div class="d-flex flex-wrap align-items-center gap-1">
            <button
              type="button"
              class="btn-admin-action btn-admin-action--toggle-all"
              @click="toggleAllSections"
            >
              {{ allExpanded ? $t('jobList.collapseAll') : $t('jobList.expandAll') }}
            </button>
            <span class="badge-stat badge-stat--section">
              {{ filteredSections.length }} {{ $t('jobList.sectionsCount') }}
            </span>
            <span class="badge-stat badge-stat--dept">
              {{ allDepartments.length }} {{ $t('jobList.deptsCount') }}
            </span>
          </div>
        </div>

        <!-- กล่องใหญ่ของแต่ละ Section (Big Box Section Cards) -->
        <div class="sections-container">
          <!-- แถบระบุกลุ่มส่วนงานที่มีข้อมูลด้านบน (Active Sections Group Banner) -->
          <div
            v-if="activeSectionsCount > 0 && !searchKeyword"
            class="sections-group-banner sections-group-banner--active d-flex align-items-center justify-content-between p-3 rounded-lg shadow-sm"
          >
            <div class="d-flex align-items-center gap-1">
              <span class="badge badge-success px-2 py-1 font-weight-bold badge-group-pill">
                <CIcon name="cil-check-circle" size="sm" class="mr-1" />
                {{ $t('jobList.activeSectionsGroup') }}
              </span>
              <span class="font-weight-bold text-dark group-title-text ml-2">
                {{ activeSectionsCount }} {{ $t('jobList.sectionsCount') }}
              </span>
            </div>
            <small class="text-muted d-none d-md-inline">
              {{ $t('jobList.activeSectionsDesc') }}
            </small>
          </div>

          <template v-for="(sec, index) in filteredSections">
            <!-- เส้นขีดคั่นแยกส่วนงานที่ไม่มีอะไรเลย กับส่วนงานที่มีฝ่ายมีประเภทงานแจ้งซ่อมอย่างชัดเจน -->
            <div
              v-if="shouldShowDivider(sec, index)"
              :key="'divider-' + sec._id"
              class="sections-group-divider my-4 pt-3 pb-2"
            >
              <div class="divider-line-container d-flex align-items-center">
                <div class="divider-line flex-grow-1"></div>
                <div class="divider-chip px-3 py-2 mx-3 shadow-sm rounded-pill d-inline-flex align-items-center">
                  <CIcon name="cil-ban" size="sm" class="text-secondary mr-2" />
                  <strong class="text-secondary mr-2 small">{{ $t('jobList.emptySectionsGroup') }}</strong>
                  <span class="badge badge-secondary badge-pill">{{ emptySectionsCount }} {{ $t('jobList.sectionsCount') }}</span>
                </div>
                <div class="divider-line flex-grow-1"></div>
              </div>
              <div class="text-center mt-2">
                <small class="text-muted font-italic">
                  {{ $t('jobList.emptySectionsHint') }}
                </small>
              </div>
            </div>

            <div
              :key="sec._id"
              class="section-big-box card mb-4 border-0"
              :class="{
                'section-box--has-data': hasData(sec),
                'section-box--empty': !hasData(sec)
              }"
            >
              <!-- Header กล่องใหญ่: ส่วนงาน (Section Header) -->
              <div
                class="card-header section-header py-3 px-4 d-flex flex-wrap justify-content-between align-items-center"
                :class="{ 'section-header--empty': !hasData(sec) }"
                @click="toggleSection(sec._id)"
                style="cursor: pointer;"
              >
                <div class="d-flex align-items-center">
                  <div
                    class="section-badge-icon mr-3 mr-md-4 shadow-sm d-flex align-items-center justify-content-center"
                    :class="{ 'section-badge-icon--empty': !hasData(sec) }"
                  >
                    <CIcon :name="hasData(sec) ? 'cil-briefcase' : 'cil-folder'" size="lg" />
                  </div>
                  <div>
                    <div class="d-flex align-items-center gap-1 flex-wrap">
                      <h5 class="mb-0 font-weight-bold text-white section-title-text">
                        {{ formatSectionName(sec) }}
                      </h5>
                      <span v-if="sec.code" class="badge-code badge-code--section">
                        {{ sec.code }}
                      </span>
                      <span v-if="hasData(sec)" class="badge-active-tag">
                        {{ $t('jobList.hasDataBadge') }}
                      </span>
                      <span v-else class="badge-empty-tag">
                        {{ $t('jobList.noDataBadge') }}
                      </span>
                    </div>
                  <div class="section-meta-text mt-1">
                    <span class="mr-3"><strong>{{ getSectionDepts(sec._id).length }}</strong> {{ $t('jobList.affiliatedDepts') }}</span>
                    <span><strong>{{ (sec.subCategories || []).length }}</strong> {{ $t('jobList.jobTypesCount') }}</span>
                  </div>
                </div>
              </div>

              <!-- Action buttons on Section Header -->
              <div class="section-actions-wrapper d-flex align-items-center gap-1 flex-wrap mt-2 mt-lg-0" @click.stop>
                <button
                  type="button"
                  class="btn-section-action btn-section-action--add-dept"
                  @click="openAddDeptModal(sec)"
                  :title="$t('jobList.addDeptTitle')"
                >
                  {{ $t('jobList.addDept') }}
                </button>
                <button
                  type="button"
                  class="btn-section-action btn-section-action--edit-sec"
                  @click="openEditSectionModal(sec)"
                  :title="$t('jobList.editSectionTitle')"
                >
                  {{ $t('jobList.editSection') }}
                </button>
                <button
                  type="button"
                  class="btn-section-action btn-section-action--delete-sec admin-action-btn"
                  @click="removeSection(sec)"
                  :title="$t('jobList.deleteSection')"
                >
                  {{ $t('mgmt.delete') }}
                </button>
                <button
                  type="button"
                  class="btn-section-action btn-section-action--toggle"
                  @click="toggleSection(sec._id)"
                >
                  {{ isSectionExpanded(sec._id) ? $t('jobList.collapse') : $t('jobList.expand') }}
                </button>
              </div>
            </div>

            <!-- Body กล่องใหญ่: แสดงเมื่อขยาย -->
            <div v-show="isSectionExpanded(sec._id)" class="card-body section-body p-3 p-md-4 bg-white">
                
                <!-- 1. กล่องยาวของแต่ละฝ่าย (Department Long Boxes) -->
                <div
                  v-for="dept in getSectionDepts(sec._id)"
                  :key="dept._id"
                  class="department-long-box card mb-3 shadow-sm"
                >
                  <!-- หัวกล่องฝ่าย (Department Header) -->
                  <div class="card-header dept-header py-3 px-3 px-md-4 d-flex flex-wrap justify-content-between align-items-center">
                    <div class="d-flex align-items-center gap-1 flex-wrap">
                      <strong class="text-dark dept-name-title font-weight-bold">
                        {{ formatDeptName(dept) }}
                      </strong>
                      <span v-if="dept.code" class="badge-code badge-code--dept">
                        {{ dept.code }}
                      </span>
                      <span
                        class="badge-status-pill"
                        :class="dept.isActive !== false ? 'badge-status-pill--active' : 'badge-status-pill--inactive'"
                      >
                        {{ dept.isActive !== false ? $t('jobList.activeStatus') : $t('jobList.inactiveStatus') }}
                      </span>
                      <span class="badge-count-pill">
                        {{ getJobsByDept(sec, dept._id).length }} {{ $t('jobList.jobsUnit') }}
                      </span>
                    </div>

                    <!-- Department Actions -->
                    <div class="dept-actions-wrapper d-flex align-items-center gap-1 flex-wrap mt-2 mt-md-0">
                      <button
                        type="button"
                        class="btn-dept-action btn-dept-action--add-job"
                        @click="openAddJobModal(sec, dept._id)"
                      >
                        {{ $t('jobList.addJobInDept') }}
                      </button>
                      <button
                        type="button"
                        class="btn-dept-action btn-dept-action--edit-dept"
                        @click="openEditDeptModal(sec, dept)"
                        :title="$t('jobList.editDept')"
                      >
                        {{ $t('jobList.editDept') }}
                      </button>
                      <button
                        type="button"
                        class="btn-dept-action btn-dept-action--delete-dept admin-action-btn"
                        @click="deleteDept(sec, dept)"
                        :title="$t('jobList.deleteDept')"
                      >
                        {{ $t('mgmt.delete') }}
                      </button>
                    </div>
                  </div>

                  <!-- เนื้อหาในฝ่าย: รายการประเภทงาน (Jobs inside this Department) -->
                  <div class="card-body p-0">
                    <!-- พื้นที่รับผิดชอบในฝ่ายนี้ (Locations under this department) -->
                    <div v-if="getDeptLocations(dept).length > 0" class="dept-locations-banner px-3 px-md-4 py-2 bg-light border-bottom d-flex align-items-center flex-wrap">
                      <small class="text-muted mr-2 font-weight-bold d-flex align-items-center">
                        <CIcon name="cil-location-pin" size="sm" class="text-primary mr-1" />
                        <span>{{ $t('jobList.responsibleAreas') || 'พื้นที่ในความรับผิดชอบ' }}:</span>
                      </small>
                      <div class="d-flex flex-wrap gap-1">
                        <span v-for="loc in getDeptLocations(dept)" :key="loc" class="badge badge-light border text-dark font-weight-normal px-2 py-1 mr-1 mb-1">
                          {{ loc }}
                        </span>
                      </div>
                    </div>
                    <div v-if="getJobsByDept(sec, dept._id).length > 0" class="table-responsive">
                      <table class="table table-hover align-middle mb-0 job-nested-table">
                        <thead class="job-table-thead">
                          <tr>
                            <th style="width: 70px;" class="text-center">{{ $t('jobList.iconCol') }}</th>
                            <th>{{ $t('jobList.jobNameCol') }}</th>
                            <th style="width: 160px;" class="text-center"><CIcon name="cil-clock" class="mr-1" />{{ $t('jobList.slaCol') }}</th>
                            <th style="width: 130px;" class="text-center">{{ $t('jobList.statusCol') }}</th>
                            <th style="width: 140px; min-width: 140px;" class="text-center"><CIcon name="cil-settings" class="mr-1" />{{ $t('jobList.actionsCol') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="job in getJobsByDept(sec, dept._id)" :key="job._id || job.name" class="job-item-row">
                            <!-- ไอคอน -->
                            <td class="text-center align-middle py-2">
                              <div
                                class="job-icon-box mx-auto"
                                @click="triggerJobIconUpload(sec, job)"
                                :title="$t('jobList.clickToUploadIcon')"
                              >
                                <img
                                  v-if="isCustomImageIcon(job.icon)"
                                  :src="getCustomImageUrl(job.icon)"
                                  class="job-icon-img"
                                  :alt="formatJobName(job)"
                                />
                                <CIcon
                                  v-else
                                  :name="getJobIconName(job)"
                                  class="job-icon-svg"
                                />
                              </div>
                            </td>
                            <!-- ชื่องาน -->
                            <td class="align-middle">
                              <span class="font-weight-bold text-dark job-title-text">{{ formatJobName(job) }}</span>
                            </td>
                            <!-- SLA -->
                            <td class="align-middle text-center">
                              <span class="badge-sla-tag">
                                {{ job.slaHours || 1 }} {{ $t('jobList.hoursUnit') }}
                              </span>
                            </td>
                            <!-- สถานะ -->
                            <td class="align-middle text-center">
                              <div class="custom-control custom-switch d-inline-block">
                                <input
                                  type="checkbox"
                                  class="custom-control-input"
                                  :id="`switch-job-${sec._id}-${job._id || job.name}`"
                                  :checked="job.isActive !== false"
                                  @change="toggleJobStatus(sec, job, $event.target.checked)"
                                />
                                <label class="custom-control-label small font-weight-bold" :for="`switch-job-${sec._id}-${job._id || job.name}`">
                                  {{ job.isActive !== false ? $t('jobList.active') : $t('jobList.inactive') }}
                                </label>
                              </div>
                            </td>
                            <!-- การจัดการ -->
                            <td class="align-middle text-center">
                              <div class="d-flex justify-content-center align-items-center gap-1">
                                <button
                                  type="button"
                                  class="btn-table-action btn-table-action--edit admin-action-btn"
                                  @click="openEditJobModal(sec, job)"
                                  :title="$t('mgmt.edit')"
                                >
                                  {{ $t('mgmt.edit') }}
                                </button>
                                <button
                                  type="button"
                                  class="btn-table-action btn-table-action--delete admin-action-btn"
                                  @click="removeJob(sec, job)"
                                  :title="$t('mgmt.delete')"
                                >
                                  {{ $t('mgmt.delete') }}
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-else class="empty-dept-box text-center py-3 text-muted small bg-light font-italic">
                      {{ $t('jobList.emptyJobsInDept') }}
                    </div>
                  </div>
                </div>

                <!-- 2. กล่องยาวของ "งานส่วนกลาง (ไม่ระบุฝ่ายย่อย)" (เฉพาะส่วนงานที่ได้รับอนุญาต) -->
                <div
                  v-if="allowsCentralJobs(sec)"
                  class="department-long-box department-long-box--central card mb-3 shadow-sm"
                >
                  <div class="card-header dept-header dept-header--central py-3 px-3 px-md-4 d-flex flex-wrap justify-content-between align-items-center">
                    <div class="d-flex align-items-center gap-1 flex-wrap">
                      <strong class="text-dark dept-name-title font-weight-bold">
                        {{ $t('jobList.centralJobsTitle') }}
                      </strong>
                      <span class="badge-count-pill">
                        {{ getCentralJobs(sec).length }} {{ $t('jobList.jobsUnit') || 'งาน' }}
                      </span>
                    </div>
                    <div class="dept-actions-wrapper d-flex align-items-center gap-1 flex-wrap mt-2 mt-md-0">
                      <button
                        type="button"
                        class="btn-dept-action btn-dept-action--add-central"
                        @click="openAddJobModal(sec, null)"
                      >
                        {{ $t('jobList.addCentralJob') }}
                      </button>
                    </div>
                  </div>

                  <div class="card-body p-0">
                    <div v-if="getCentralJobs(sec).length > 0" class="table-responsive">
                      <table class="table table-hover align-middle mb-0 job-nested-table">
                        <thead class="job-table-thead">
                          <tr>
                            <th style="width: 70px;" class="text-center">{{ $t('jobList.iconCol') }}</th>
                            <th>{{ $t('jobList.jobNameCol') }}</th>
                            <th style="width: 160px;" class="text-center"><CIcon name="cil-clock" class="mr-1" />{{ $t('jobList.slaCol') }}</th>
                            <th style="width: 130px;" class="text-center">{{ $t('jobList.statusCol') }}</th>
                            <th style="width: 140px; min-width: 140px;" class="text-center"><CIcon name="cil-settings" class="mr-1" />{{ $t('jobList.actionsCol') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="job in getCentralJobs(sec)" :key="job._id || job.name" class="job-item-row">
                            <td class="text-center align-middle py-2">
                              <div
                                class="job-icon-box mx-auto"
                                @click="triggerJobIconUpload(sec, job)"
                                :title="$t('jobList.clickToUploadIcon')"
                              >
                                <img
                                  v-if="isCustomImageIcon(job.icon)"
                                  :src="getCustomImageUrl(job.icon)"
                                  class="job-icon-img"
                                  :alt="formatJobName(job)"
                                />
                                <CIcon
                                  v-else
                                  :name="getJobIconName(job)"
                                  class="job-icon-svg"
                                />
                              </div>
                            </td>
                            <td class="align-middle">
                              <span class="font-weight-bold text-dark job-title-text">{{ formatJobName(job) }}</span>
                            </td>
                            <td class="align-middle text-center">
                              <span class="badge-sla-tag">
                                {{ job.slaHours || 1 }} {{ $t('jobList.hoursUnit') }}
                              </span>
                            </td>
                            <td class="align-middle text-center">
                              <div class="custom-control custom-switch d-inline-block">
                                <input
                                  type="checkbox"
                                  class="custom-control-input"
                                  :id="`switch-job-${sec._id}-${job._id || job.name}`"
                                  :checked="job.isActive !== false"
                                  @change="toggleJobStatus(sec, job, $event.target.checked)"
                                />
                                <label class="custom-control-label small font-weight-bold" :for="`switch-job-${sec._id}-${job._id || job.name}`">
                                  {{ job.isActive !== false ? $t('jobList.active') : $t('jobList.inactive') }}
                                </label>
                              </div>
                            </td>
                            <td class="align-middle text-center">
                              <div class="d-flex justify-content-center align-items-center gap-1">
                                <button
                                  type="button"
                                  class="btn-table-action btn-table-action--edit admin-action-btn"
                                  @click="openEditJobModal(sec, job)"
                                  :title="$t('mgmt.edit')"
                                >
                                  {{ $t('mgmt.edit') }}
                                </button>
                                <button
                                  type="button"
                                  class="btn-table-action btn-table-action--delete admin-action-btn"
                                  @click="removeJob(sec, job)"
                                  :title="$t('mgmt.delete')"
                                >
                                  {{ $t('mgmt.delete') }}
                                </button>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-else class="empty-dept-box text-center py-2 text-muted small bg-light font-italic">
                      {{ $t('jobList.emptyCentralJobs') }}
                    </div>
                  </div>
                </div>

                <!-- Empty State if no departments in section -->
                <div v-if="getSectionDepts(sec._id).length === 0" class="empty-section-alert text-center py-4 bg-light rounded-lg border border-dashed">
                  <div class="font-weight-bold text-dark">{{ $t('jobList.emptyDepts') }}</div>
                  <div class="small text-muted mb-3">{{ $t('jobList.emptyDeptsSub') }}</div>
                  <button type="button" class="btn btn-sm btn-primary font-weight-bold px-3 py-2 shadow-sm rounded-lg" @click="openAddDeptModal(sec)">
                    {{ $t('jobList.addFirstDept') }}
                  </button>
                </div>

              </div>
          </div>
          </template>

          <!-- Empty state when search or data is empty -->
          <div v-if="filteredSections.length === 0" class="text-center py-5 bg-white border shadow-sm rounded-lg">
            <h5 class="text-dark font-weight-bold">{{ $t('jobList.notFoundTitle') }}</h5>
            <p class="text-muted small mb-0">{{ $t('jobList.notFoundSub') }}</p>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Hidden Input for Icon Upload -->
    <CModal :show.sync="showIconAttachment" :title="$t('sendReport.imageLabel')" centered>
      <ImageAttachment :preview="currentUploadingJob && isCustomImageIcon(currentUploadingJob.icon) ? getCustomImageUrl(currentUploadingJob.icon) : ''"
        :disabled="iconUploading" :removable="Boolean(currentUploadingJob && isCustomImageIcon(currentUploadingJob.icon))" @change="handleJobIconUpload" @remove="handleRemoveJobIcon" />
      <template #footer><button type="button" class="btn btn-outline-secondary rounded-lg" :disabled="iconUploading" @click="showIconAttachment = false">{{ $t('mgmt.cancel') }}</button></template>
    </CModal>

    <!-- MODAL 1: เพิ่ม / แก้ไข ส่วนงาน (Section Modal) -->
    <CModal size="md" :show.sync="sectionModal.show" centered :close-button="false">
      <template #header>
        <h5 class="modal-title mb-0 font-weight-bold">{{ sectionModal.isEditing ? $t('jobList.editSectionTitle') : $t('jobList.addSectionTitle') }}</h5>
      </template>
      <div class="p-2">
        <CInput
          :placeholder="$t('jobList.sectionCodePlaceholder')"
          v-model="sectionModal.code"
        >
          <template #label>
            <label class="font-weight-bold">{{ $t('jobList.sectionCodeLabel') }} <span class="text-danger">*</span></label>
          </template>
        </CInput>
        <CInput
          :placeholder="$t('jobList.sectionNamePlaceholder')"
          v-model="sectionModal.name"
        >
          <template #label>
            <label class="font-weight-bold">{{ $t('jobList.sectionNameLabel') }} <span class="text-danger">*</span></label>
          </template>
        </CInput>
        <CInput
          :placeholder="$t('jobList.sectionNameEnPlaceholder')"
          v-model="sectionModal.nameEn"
        >
          <template #label>
            <label class="font-weight-bold">{{ $t('jobList.sectionNameEnLabel') }}</label>
          </template>
        </CInput>
      </div>
      <template #footer>
        <CButton color="secondary" class="admin-action-btn" @click="sectionModal.show = false">
          {{ $t('mgmt.cancel') }}
        </CButton>
        <CButton color="primary" class="font-weight-bold admin-action-btn" @click="saveSection">
          {{ sectionModal.isEditing ? $t('jobList.saveSectionBtn') : $t('jobList.createSectionBtn') }}
        </CButton>
      </template>
    </CModal>

    <!-- MODAL 2: เพิ่ม / แก้ไข ฝ่าย (Department Modal) -->
    <CModal size="md" :show.sync="deptModal.show" centered :close-button="false">
      <template #header>
        <h5 class="modal-title mb-0 font-weight-bold">{{ deptModal.isEditing ? $t('jobList.editDeptTitle') : $t('jobList.addDeptTitle') }}</h5>
      </template>
      <div class="p-2" v-if="deptModal.section">
        <div class="alert alert-info py-2 px-3 small font-weight-bold mb-3 rounded-lg">
          {{ $t('jobList.affiliatedSection') }} {{ formatSectionName(deptModal.section) }} ({{ deptModal.section.code || '-' }})
        </div>
        <CInput
          :placeholder="$t('jobList.deptCodePlaceholder')"
          v-model="deptModal.code"
        >
          <template #label>
            <label class="font-weight-bold">{{ $t('jobList.deptCodeLabel') }} <span class="text-danger">*</span></label>
          </template>
        </CInput>
        <CInput
          :placeholder="$t('jobList.deptNamePlaceholder')"
          v-model="deptModal.name"
        >
          <template #label>
            <label class="font-weight-bold">{{ $t('jobList.deptNameLabel') }} <span class="text-danger">*</span></label>
          </template>
        </CInput>
        <CInput
          :placeholder="$t('jobList.deptNameEnPlaceholder')"
          v-model="deptModal.nameEn"
        >
          <template #label>
            <label class="font-weight-bold">{{ $t('jobList.deptNameEnLabel') }}</label>
          </template>
        </CInput>
        <div class="custom-control custom-switch mt-2">
          <input type="checkbox" class="custom-control-input" id="deptActiveSwitch" v-model="deptModal.isActive" />
          <label class="custom-control-label small font-weight-bold" for="deptActiveSwitch">
            {{ deptModal.isActive ? $t('jobList.deptActiveSwitch') : $t('jobList.deptInactiveSwitch') }}
          </label>
        </div>
      </div>
      <template #footer>
        <CButton color="secondary" class="admin-action-btn" @click="deptModal.show = false">
          {{ $t('mgmt.cancel') }}
        </CButton>
        <CButton color="primary" class="font-weight-bold admin-action-btn" @click="saveDept">
          {{ deptModal.isEditing ? $t('jobList.saveDeptBtn') : $t('jobList.createDeptBtn') }}
        </CButton>
      </template>
    </CModal>

    <!-- MODAL 3: เพิ่ม / แก้ไข ประเภทงานแจ้งซ่อม (Job Type Modal) -->
    <CModal size="md" :show.sync="jobModal.show" centered :close-button="false">
      <template #header>
        <h5 class="modal-title mb-0 font-weight-bold">{{ jobModal.isEditing ? $t('jobList.editJobTitle') : $t('jobList.addJobTitle') }}</h5>
      </template>
      <div class="p-2" v-if="jobModal.section">
        <div class="alert alert-light border py-2 px-3 small mb-3 rounded-lg">
          <div><strong>{{ $t('jobList.sectionColon') }}</strong> {{ formatSectionName(jobModal.section) }}</div>
          <div v-if="jobModal.departmentId">
            <strong>{{ $t('jobList.deptColon') }}</strong> {{ getDeptNameById(jobModal.departmentId) }}
          </div>
          <div v-else class="text-info">
            <strong>{{ $t('jobList.deptColon') }}</strong> {{ $t('jobList.centralOption') }}
          </div>
        </div>

        <CInput
          :placeholder="$t('jobList.jobNamePlaceholder')"
          v-model="jobModal.name"
        >
          <template #label>
            <label class="font-weight-bold">{{ $t('jobList.jobNameLabel') }} <span class="text-danger">*</span></label>
          </template>
        </CInput>
        <CInput
          :placeholder="$t('jobList.jobNameEnPlaceholder')"
          v-model="jobModal.nameEn"
        >
          <template #label>
            <label class="font-weight-bold">{{ $t('jobList.jobNameEnLabel') }}</label>
          </template>
        </CInput>

        <div class="form-group">
          <label class="font-weight-bold small">{{ $t('jobList.slaLabel') }} <span class="text-danger">*</span></label>
          <div class="input-group">
            <input
              type="number"
              min="1"
              class="form-control font-weight-bold"
              v-model.number="jobModal.slaHours"
            />
            <div class="input-group-append">
              <span class="input-group-text font-weight-bold">{{ $t('jobList.hoursUnit') }}</span>
            </div>
          </div>
        </div>

        <div class="form-group" v-if="getSectionDepts(jobModal.section._id).length > 0 || allowsCentralJobs(jobModal.section)">
          <label class="font-weight-bold small">{{ $t('jobList.deptSelectLabel') }}</label>
          <select class="form-control" v-model="jobModal.departmentId">
            <option v-if="allowsCentralJobs(jobModal.section)" :value="null">{{ $t('jobList.centralOption') }}</option>
            <option
              v-for="d in getSectionDepts(jobModal.section._id)"
              :key="d._id"
              :value="d._id"
            >
              {{ formatDeptName(d) }} ({{ d.code || '-' }})
            </option>
          </select>
        </div>

        <div class="custom-control custom-switch mt-3">
          <input type="checkbox" class="custom-control-input" id="jobActiveSwitch" v-model="jobModal.isActive" />
          <label class="custom-control-label small font-weight-bold" for="jobActiveSwitch">
            {{ jobModal.isActive ? $t('jobList.jobActiveSwitch') : $t('jobList.jobInactiveSwitch') }}
          </label>
        </div>
      </div>
      <template #footer>
        <CButton color="secondary" class="admin-action-btn" @click="jobModal.show = false">
          {{ $t('mgmt.cancel') }}
        </CButton>
        <CButton color="primary" class="font-weight-bold admin-action-btn" @click="saveJob">
          {{ jobModal.isEditing ? $t('jobList.saveJobBtn') : $t('jobList.createJobBtn') }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ImageAttachment from "@/components/common/ImageAttachment.vue";
import axios from "axios";
import Swal from "sweetalert2";
import { formatAdminSwalButton } from "@/utils/sweetalertButtonHelper";
import { translateSection, translateDepartment, translateJobType, JOB_TYPE_TRANSLATIONS } from "@/utils/categoryi18n";


export default {
  name: "HierarchicalJobTypeManagement",
  components: { ImageAttachment },
  data() {
    return {
      showIconAttachment: false,
      iconUploading: false,
      categoriesData: [],
      allDepartments: [],
      searchKeyword: "",
      appUrl: process.env.VUE_APP_APP_URL || "",
      expandedSections: {},
      allExpanded: true,

      // Modals
      sectionModal: {
        show: false,
        isEditing: false,
        id: null,
        code: "",
        name: "",
        nameEn: "",
      },
      deptModal: {
        show: false,
        isEditing: false,
        section: null,
        id: null,
        code: "",
        name: "",
        nameEn: "",
        isActive: true,
      },
      jobModal: {
        show: false,
        isEditing: false,
        section: null,
        originalJob: null,
        name: "",
        nameEn: "",
        slaHours: 2,
        departmentId: null,
        isActive: true,
      },
      currentUploadingSec: null,
      currentUploadingJob: null,
    };
  },

  created() {
    this.loadData();
  },

  computed: {
    activeSectionsCount() {
      return this.filteredSections.filter((s) => this.hasData(s)).length;
    },
    emptySectionsCount() {
      return this.filteredSections.filter((s) => !this.hasData(s)).length;
    },
    // กรองและจัดเรียง: นำส่วนงานที่มีฝ่ายและประเภทงานแจ้งซ่อมขึ้นมาไว้บนสุด
    filteredSections() {
      const kw = this.searchKeyword.toLowerCase().trim();
      let list = [...this.categoriesData];

      if (kw) {
        list = list.filter((sec) => {
          const secMatch =
            (sec.departmentName || "").toLowerCase().includes(kw) ||
            (sec.code || "").toLowerCase().includes(kw);

          const depts = this.getSectionDepts(sec._id);
          const deptMatch = depts.some(
            (d) =>
              (d.name || "").toLowerCase().includes(kw) ||
              (d.code || "").toLowerCase().includes(kw)
          );

          const jobMatch = (sec.subCategories || []).some((j) =>
            (j.name || "").toLowerCase().includes(kw)
          );

          return secMatch || deptMatch || jobMatch;
        });
      }

      // จัดเรียง: ส่วนงานที่มีฝ่ายและประเภทงานแจ้งซ่อมขึ้นมาไว้ข้างบน
      list.sort((a, b) => {
        const aHasData = this.hasData(a);
        const bHasData = this.hasData(b);

        // 1. กลุ่มที่มีข้อมูลฝ่าย/ประเภทงานแจ้งซ่อม ต้องอยู่ด้านบนเสมอ
        if (aHasData !== bHasData) {
          return aHasData ? -1 : 1;
        }

        // 2. ถ้ามีข้อมูลเหมือนกัน จัดเรียงตามความพร้อมของข้อมูล (มีทั้งฝ่ายและงาน > จำนวนงาน)
        if (aHasData && bHasData) {
          const aDepts = this.getSectionDepts(a._id).length;
          const bDepts = this.getSectionDepts(b._id).length;
          const aJobs = (a.subCategories || []).length;
          const bJobs = (b.subCategories || []).length;

          const aScore = (aDepts > 0 && aJobs > 0 ? 1000 : 0) + aJobs * 10 + aDepts;
          const bScore = (bDepts > 0 && bJobs > 0 ? 1000 : 0) + bJobs * 10 + bDepts;

          if (bScore !== aScore) {
            return bScore - aScore; // มากกว่าอยู่บน
          }
        }

        // 3. จัดเรียงตามตัวอักษรชื่อภาษาไทย
        return (a.departmentName || "").localeCompare(b.departmentName || "", "th");
      });

      return list;
    },
  },

  methods: {
    formatSectionName(sec) {
      if (!sec) return "-";
      const lang = this.$store?.state?.locale?.lang || "th";
      return translateSection(sec, lang);
    },
    formatDeptName(dept) {
      if (!dept) return "-";
      const lang = this.$store?.state?.locale?.lang || "th";
      return translateDepartment(dept, lang);
    },
    formatJobName(job) {
      if (!job) return "-";
      const lang = this.$store?.state?.locale?.lang || "th";
      return translateJobType(job, lang);
    },
    isCustomImageIcon(icon) {
      if (!icon || typeof icon !== 'string') return false;
      return icon.startsWith('/') || icon.startsWith('http://') || icon.startsWith('https://');
    },
    getCustomImageUrl(icon) {
      if (!icon) return '';
      if (icon.startsWith('http://') || icon.startsWith('https://')) return icon;
      return `${this.appUrl}${icon}`;
    },
    getJobIconName(job) {
      if (!job) return 'cil-settings';
      if (job.icon && typeof job.icon === 'string' && /^(cil-|cib-|cif-)/.test(job.icon)) {
        return job.icon;
      }
      const raw = typeof job === 'object' ? (job.name || job.title) : String(job);
      return this.getJobIcon(raw);
    },
    getJobIcon(name) {
      if (!name) return 'cil-settings';
      const key = String(name).trim();
      const thaiName = JOB_TYPE_TRANSLATIONS && JOB_TYPE_TRANSLATIONS[key]?.th ? JOB_TYPE_TRANSLATIONS[key].th : key;
      const n = (thaiName + ' ' + key).toLowerCase();

      // กล้องวงจรปิด / CCTV
      if (n.includes('กล้อง') || n.includes('cctv') || n.includes('camera')) return 'cil-camera';
      // เคเบิลทีวี / Cable TV
      if ((n.includes('cable') || n.includes('tv') || n.includes('เคเบิล') || n.includes('เคเบิ้ล') || n.includes('ทีวี') || n.includes('television')) && !n.includes('cctv')) return 'cil-tv';

      // คอมพิวเตอร์ / PC / จอภาพ
      if (n.includes('คอมพิวเตอร์') || n.includes('เครื่องคอม') || n.includes('computer') || n.includes('monitor') || n.includes('pc') || n.includes('laptop')) return 'cil-monitor';
      // อินเทอร์เน็ต / Wi-Fi
      if (n.includes('wi-fi') || n.includes('wifi') || n.includes('อินเทอร์เน็ต') || n.includes('เครือข่าย') || n.includes('internet') || n.includes('network') || n.includes('lan')) return 'cil-wifi-signal-1';
      // เครื่องพิมพ์ / สแกนเนอร์
      if (n.includes('เครื่องพิมพ์') || n.includes('สแกนเนอร์') || n.includes('ปริ้น') || n.includes('printer') || n.includes('scanner') || n.includes('print')) return 'cil-print';
      // แอร์ / ปรับอากาศ
      if (n.includes('ปรับอากาศ') || n.includes('เครื่องปรับอากาศ') || n.includes('ระบบเครื่องกล') || n.includes('น้ำเย็น') || n.includes('แอร์') || n.includes('air') || n.includes('cooling')) return 'cil-snowflake';
      // หลอดไฟ / แสงสว่าง
      if (n.includes('หลอดไฟ') || n.includes('แสงสว่าง') || n.includes('lighting') || n.includes('lamp') || n.includes('bulb') || n.includes('โคมไฟ')) return 'cil-lightbulb';
      // ปลั๊กไฟ / สวิตช์ / ไฟฟ้า
      if (n.includes('ปลั๊ก') || n.includes('สวิตช์') || n.includes('เต้ารับ') || n.includes('ไฟฟ้า') || n.includes('electric') || n.includes('power')) return 'cil-bolt';
      // ประปา / ท่อน้ำ / ก๊อกน้ำ / สุขาภิบาล
      if (n.includes('ท่อ') || n.includes('ก๊อก') || n.includes('น้ำรั่ว') || n.includes('น้ำไม่ไหล') || n.includes('น้ำไม่') || n.includes('ประปา') || n.includes('สุขาภิบาล') || n.includes('ชักโครก') || n.includes('plumb') || n.includes('sanitation') || n.includes('water') || n.includes('leak') || n.includes('drain') || n.includes('tap') || n.includes('toilet')) return 'cil-drop';
      // ประตู / หน้าต่าง / ลิฟต์ / ลูกบิด
      if (n.includes('ลิฟต์') || n.includes('ลิฟท์') || n.includes('ประตู') || n.includes('หน้าต่าง') || n.includes('ลูกบิด') || n.includes('กลอน') || n.includes('กุญแจ') || n.includes('door') || n.includes('elevator') || n.includes('window')) return 'cil-door';
      // อาคาร / โยธา / ผนัง / พื้น / เพดาน
      if (n.includes('อาคาร') || n.includes('โยธา') || n.includes('สถาปัตย์') || n.includes('ผนัง') || n.includes('พื้น') || n.includes('เพดาน') || n.includes('ร้าว') || n.includes('หลุดร่อน') || n.includes('กระเบื้อง') || n.includes('building') || n.includes('civil') || n.includes('wall') || n.includes('floor')) return 'cil-building';
      // โทรศัพท์
      if (n.includes('โทรศัพท์') || n.includes('phone') || n.includes('telephone')) return 'cil-phone';
      // เฟอร์นิเจอร์ / ครุภัณฑ์ / โต๊ะ / เก้าอี้
      if (n.includes('เฟอร์นิเจอร์') || n.includes('ครุภัณฑ์') || n.includes('วัสดุ') || n.includes('โต๊ะ') || n.includes('เก้าอี้') || n.includes('furniture') || n.includes('couch') || n.includes('chair') || n.includes('desk')) return 'cil-couch';
      // ความสะอาด / สิ่งแวดล้อม / ขยะ
      if (n.includes('ความสะอาด') || n.includes('สิ่งแวดล้อม') || n.includes('ขยะ') || n.includes('trash') || n.includes('clean') || n.includes('environment')) return 'cil-trash';
      // ความปลอดภัย / อัคคีภัย
      if (n.includes('ความปลอดภัย') || n.includes('อัคคีภัย') || n.includes('ดับเพลิง') || n.includes('safety') || n.includes('fire') || n.includes('lock')) return 'cil-lock-locked';
      // โสต / ภาพ / เสียง
      if (n.includes('โสต') || n.includes('ภาพ') || n.includes('เสียง') || n.includes('audio') || n.includes('sound') || n.includes('mic')) return 'cil-audio';
      // ห้องเรียน / โปรเจกเตอร์
      if (n.includes('ห้องเรียน') || n.includes('โปรเจกเตอร์') || n.includes('การสอนออนไลน์') || n.includes('projector')) return 'cil-screen-desktop';
      // บัญชี / อีเมล / google
      if (n.includes('บัญชีผู้ใช้') || n.includes('อีเมล') || n.includes('google') || n.includes('email') || n.includes('account')) return 'cil-at';
      // ระบบสารสนเทศ / ซอฟต์แวร์
      if (n.includes('ระบบสารสนเทศ') || n.includes('ซอฟต์แวร์') || n.includes('software') || n.includes('system') || n.includes('code')) return 'cil-code';

      // อื่น ๆ
      return 'cil-settings';
    },
    async loadData() {
      await Promise.all([this.loadCategories(), this.loadAllDepartments()]);
      // ขยายเฉพาะ 3 ส่วนงานหลักที่ได้รับอนุญาต และหุบส่วนงานอื่น ๆ ทั้งหมดไว้เป็นค่าเริ่มต้น
      const expandMap = {};
      this.categoriesData.forEach((s) => {
        expandMap[s._id] = this.allowsCentralJobs(s);
      });
      this.expandedSections = expandMap;
      this.allExpanded = false;
    },

    async loadCategories() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_APP_URL}/category`);
        this.categoriesData = res.data.data || [];
      } catch (err) {
        console.error("Load categories error:", err);
      }
    },

    async loadAllDepartments() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_APP_URL}/departments/all`);
        this.allDepartments = res.data?.data || res.data || [];
      } catch (err) {
        console.error("Load all departments error:", err);
      }
    },

    hasData(sec) {
      if (!sec) return false;
      const depts = this.getSectionDepts(sec._id);
      const jobs = sec.subCategories || [];
      return jobs.length > 0 || depts.length > 1;
    },

    shouldShowDivider(sec, index) {
      if (index === 0) return false;
      const currentHasData = this.hasData(sec);
      const prevSec = this.filteredSections[index - 1];
      const prevHasData = prevSec ? this.hasData(prevSec) : false;
      return !currentHasData && prevHasData;
    },

    getSectionDepts(sectionId) {
      if (!sectionId || !Array.isArray(this.allDepartments)) return [];
      return this.allDepartments.filter(
        (d) => d.section && (d.section._id === sectionId || d.section === sectionId)
      );
    },

    getDeptNameById(deptId) {
      const found = this.allDepartments.find((d) => d._id === deptId);
      return found ? `${this.formatDeptName(found)} (${found.code || '-'})` : "-";
    },

    getJobsByDept(section, deptId) {
      if (!section || !Array.isArray(section.subCategories)) return [];
      return section.subCategories.filter((job) => {
        const jDeptId =
          typeof job.department === "object" ? job.department?._id : job.department;
        return jDeptId === deptId;
      });
    },

    getCentralJobs(section) {
      if (!section || !Array.isArray(section.subCategories)) return [];
      return section.subCategories.filter((job) => {
        const jDeptId =
          typeof job.department === "object" ? job.department?._id : job.department;
        return !jDeptId;
      });
    },

    getDeptLocations(dept) {
      if (!dept || !dept.code) return [];
      const locationMap = {
        'ASSET-ACCOM': [
          'อาคารวนาเวศน์'
        ],
        'ASSET-BOOK': [
          'สำนักงานศูนย์หนังสือ',
          'M-Store'
        ],
        'ASSET-FOOD': [
          'ศูนย์อาหาร E1',
          'ศูนย์อาหาร E2',
          'ศูนย์อาหาร C5',
          'ศูนย์อาหาร D1',
          'ศูนย์อาหาร M-Square',
          'ครัวลำดวน',
          'โถงร้านค้า S2',
          'ร้านชงชาอาคาร AV',
          'เรือนริมน้ำ'
        ],
        'ASSET-RENT': [
          'กลุ่มร้านค้าอาคาร M-Square',
          'ร้าน Trois Monts',
          'ร้านมินิมาร์ทอาคารสันทนาการ',
          'ร้านซักอบรีด',
          'เครื่องซักผ้าหยอดเหรียญ',
          'คาร์แคร์อาคาร E4',
          'ร้านมาม่าคาเฟ่อาคาร E4',
          'ตู้หยอดเหรียญสินค้าอัตโนมัติ',
          'ร้านมินิมาร์ทหอพักนานาชาติ',
          'ร้านทำเล็บหอพักนานาชาติ'
        ],
      };
      return locationMap[dept.code] || [];
    },

    allowsCentralJobs(section) {
      if (!section) return false;
      const name = (section.departmentName || "").trim();
      const allowed = [
        "ส่วนอาคารสถานที่",
        "ส่วนอาคารและสถานที่",
        "ศูนย์เทคโนโลยีดิจิทัลและสารสนเทศ",
        "ศูนย์เทคโนโลยีและสารสนเทศ",
        "ศูนย์บริการวิชาการ",
        "สำนักงานจัดการทรัพย์สินและรายได้",
      ];
      return allowed.some((a) => name.includes(a) || a.includes(name));
    },

    isSectionExpanded(secId) {
      return Boolean(this.expandedSections[secId]);
    },

    toggleSection(secId) {
      this.$set(this.expandedSections, secId, !this.isSectionExpanded(secId));
    },

    toggleAllSections() {
      this.allExpanded = !this.allExpanded;
      const map = {};
      this.categoriesData.forEach(s => {
        map[s._id] = this.allExpanded;
      });
      this.expandedSections = map;
    },

    // ----------------------------------------------------
    // Section Operations
    // ----------------------------------------------------
    openAddSectionModal() {
      this.sectionModal = {
        show: true,
        isEditing: false,
        id: null,
        code: "",
        name: "",
        nameEn: "",
      };
    },

    openEditSectionModal(sec) {
      this.sectionModal = {
        show: true,
        isEditing: true,
        id: sec._id,
        code: sec.code || "",
        name: sec.departmentName || "",
        nameEn: sec.nameEn || "",
      };
    },

    async saveSection() {
      if (!this.sectionModal.name.trim()) {
        Swal.fire(this.$t("mgmt.warn"), this.$t("jobList.sectionNameRequired"), "warning");
        return;
      }

      const payload = {
        departmentName: this.sectionModal.name.trim(),
        nameEn: this.sectionModal.nameEn ? this.sectionModal.nameEn.trim() : null,
        code: this.sectionModal.code ? this.sectionModal.code.trim().toUpperCase() : undefined,
      };

      try {
        if (this.sectionModal.isEditing) {
          await axios.put(`${process.env.VUE_APP_APP_URL}/category`, {
            _id: this.sectionModal.id,
            ...payload,
          });
          Swal.fire({ icon: "success", title: this.$t("jobList.saveSectionSuccess"), toast: true, position: "top-end", showConfirmButton: false, timer: 1500 });
        } else {
          await axios.post(`${process.env.VUE_APP_APP_URL}/category`, payload);
          Swal.fire({ icon: "success", title: this.$t("jobList.saveSectionSuccess"), toast: true, position: "top-end", showConfirmButton: false, timer: 1500 });
        }
        this.sectionModal.show = false;
        await this.loadData();
      } catch (err) {
        Swal.fire(this.$t("mgmt.error") || "ข้อผิดพลาด", err.response?.data?.message || "ไม่สามารถบันทึกส่วนงานได้", "error");
      }
    },

    async removeSection(sec) {
      const confirm = await Swal.fire({
        title: this.$t("jobList.deleteSectionConfirm"),
        text: `ต้องการลบส่วนงาน: ${sec.departmentName} (${sec.code || "-"})?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: formatAdminSwalButton('trash', this.$t("mgmt.confirmDelete") || "ยืนยันการลบ"),
        cancelButtonText: formatAdminSwalButton('ban', this.$t("mgmt.cancel") || "ยกเลิก"),
      });
      if (!confirm.isConfirmed) return;

      try {
        await axios.delete(`${process.env.VUE_APP_APP_URL}/category/${sec._id}`);
        Swal.fire({ icon: "success", title: this.$t("jobList.deleteSectionSuccess"), toast: true, position: "top-end", showConfirmButton: false, timer: 1500 });
        await this.loadData();
      } catch (err) {
        Swal.fire(this.$t("mgmt.error") || "ข้อผิดพลาด", err.response?.data?.message || "ลบส่วนงานไม่สำเร็จ", "error");
      }
    },

    // ----------------------------------------------------
    // Department Operations
    // ----------------------------------------------------
    openAddDeptModal(sec) {
      this.deptModal = {
        show: true,
        isEditing: false,
        section: sec,
        id: null,
        code: "",
        name: "",
        nameEn: "",
        isActive: true,
      };
    },

    openEditDeptModal(sec, dept) {
      this.deptModal = {
        show: true,
        isEditing: true,
        section: sec,
        id: dept._id,
        code: dept.code || "",
        name: dept.name || "",
        nameEn: dept.nameEn || "",
        isActive: dept.isActive !== false,
      };
    },

    async saveDept() {
      if (!this.deptModal.code.trim() || !this.deptModal.name.trim()) {
        Swal.fire(this.$t("mgmt.warn"), this.$t("jobList.deptCodeAndNameRequired"), "warning");
        return;
      }

      const secId = this.deptModal.section._id;
      const payload = {
        section: secId,
        sectionId: secId,
        code: this.deptModal.code.trim().toUpperCase(),
        name: this.deptModal.name.trim(),
        nameEn: this.deptModal.nameEn ? this.deptModal.nameEn.trim() : null,
        isActive: this.deptModal.isActive,
      };

      try {
        if (this.deptModal.isEditing) {
          await axios.put(`${process.env.VUE_APP_APP_URL}/departments/${this.deptModal.id}`, payload);
          Swal.fire({ icon: "success", title: this.$t("jobList.saveDeptSuccess"), toast: true, position: "top-end", showConfirmButton: false, timer: 1500 });
        } else {
          await axios.post(`${process.env.VUE_APP_APP_URL}/departments`, payload);
          Swal.fire({ icon: "success", title: this.$t("jobList.saveDeptSuccess"), toast: true, position: "top-end", showConfirmButton: false, timer: 1500 });
        }
        this.deptModal.show = false;
        await this.loadAllDepartments();
      } catch (err) {
        Swal.fire(this.$t("mgmt.error") || "ข้อผิดพลาด", err.response?.data?.message || "ไม่สามารถบันทึกฝ่ายได้", "error");
      }
    },

    async deleteDept(sec, dept) {
      const confirm = await Swal.fire({
        title: this.$t("jobList.deleteDeptConfirm"),
        text: `ต้องการลบฝ่าย: ${dept.name} (${dept.code || "-"})?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: formatAdminSwalButton('trash', this.$t("mgmt.confirmDelete") || "ยืนยันการลบ"),
        cancelButtonText: formatAdminSwalButton('ban', this.$t("mgmt.cancel") || "ยกเลิก"),
      });
      if (!confirm.isConfirmed) return;

      try {
        await axios.delete(`${process.env.VUE_APP_APP_URL}/departments/${dept._id}`);
        Swal.fire({ icon: "success", title: this.$t("jobList.deleteDeptSuccess"), toast: true, position: "top-end", showConfirmButton: false, timer: 1500 });
        await this.loadAllDepartments();
      } catch (err) {
        Swal.fire(this.$t("mgmt.error") || "ข้อผิดพลาด", err.response?.data?.message || "ไม่สามารถลบฝ่ายได้", "error");
      }
    },

    // ----------------------------------------------------
    // Job Type Operations
    // ----------------------------------------------------
    openAddJobModal(sec, deptId) {
      let targetDeptId = deptId;
      const depts = this.getSectionDepts(sec._id);
      if (!targetDeptId && !this.allowsCentralJobs(sec)) {
        targetDeptId = depts.length > 0 ? depts[0]._id : null;
      }
      this.jobModal = {
        show: true,
        isEditing: false,
        section: sec,
        originalJob: null,
        name: "",
        nameEn: "",
        slaHours: 2,
        departmentId: targetDeptId,
        isActive: true,
      };
    },

    openEditJobModal(sec, job) {
      const dId = typeof job.department === "object" ? job.department?._id : job.department;
      this.jobModal = {
        show: true,
        isEditing: true,
        section: sec,
        originalJob: job,
        name: job.name || "",
        nameEn: job.nameEn || "",
        slaHours: job.slaHours || 2,
        departmentId: dId || null,
        isActive: job.isActive !== false,
      };
    },

    async saveJob() {
      if (!this.jobModal.name.trim() || !this.jobModal.slaHours) {
        Swal.fire(this.$t("mgmt.warn"), this.$t("jobList.jobNameAndSlaRequired"), "warning");
        return;
      }

      const sec = this.jobModal.section;
      const subCategories = JSON.parse(JSON.stringify(sec.subCategories || []));

      // Normalize department IDs
      subCategories.forEach((s) => {
        if (s.department && typeof s.department === "object" && s.department._id) {
          s.department = s.department._id;
        }
      });

      if (this.jobModal.isEditing && this.jobModal.originalJob) {
        const idx = subCategories.findIndex(
          (s) => s._id === this.jobModal.originalJob._id || s.name === this.jobModal.originalJob.name
        );
        if (idx !== -1) {
          subCategories[idx].name = this.jobModal.name.trim();
          subCategories[idx].nameEn = this.jobModal.nameEn ? this.jobModal.nameEn.trim() : null;
          subCategories[idx].slaHours = this.jobModal.slaHours;
          subCategories[idx].department = this.jobModal.departmentId || null;
          subCategories[idx].isActive = this.jobModal.isActive;
        }
      } else {
        subCategories.push({
          name: this.jobModal.name.trim(),
          nameEn: this.jobModal.nameEn ? this.jobModal.nameEn.trim() : null,
          slaHours: this.jobModal.slaHours,
          department: this.jobModal.departmentId || null,
          isActive: this.jobModal.isActive,
          icon: null,
        });
      }

      try {
        await axios.put(`${process.env.VUE_APP_APP_URL}/category`, {
          _id: sec._id,
          departmentName: sec.departmentName,
          code: sec.code,
          subCategories,
        });
        Swal.fire({ icon: "success", title: this.$t("jobList.saveJobSuccess"), toast: true, position: "top-end", showConfirmButton: false, timer: 1500 });
        this.jobModal.show = false;
        await this.loadCategories();
      } catch (err) {
        Swal.fire(this.$t("mgmt.error") || "ข้อผิดพลาด", err.response?.data?.message || "ไม่สามารถบันทึกประเภทงานได้", "error");
      }
    },

    async toggleJobStatus(sec, job, isActive) {
      const subCategories = JSON.parse(JSON.stringify(sec.subCategories || []));
      subCategories.forEach((s) => {
        if (s.department && typeof s.department === "object" && s.department._id) {
          s.department = s.department._id;
        }
        if (s._id === job._id || s.name === job.name) {
          s.isActive = isActive;
        }
      });

      try {
        await axios.put(`${process.env.VUE_APP_APP_URL}/category`, {
          _id: sec._id,
          departmentName: sec.departmentName,
          code: sec.code,
          subCategories,
        });
        job.isActive = isActive;
      } catch (err) {
        console.error("Toggle job status error:", err);
      }
    },

    async removeJob(sec, job) {
      const confirm = await Swal.fire({
        title: this.$t("jobList.deleteJobConfirm"),
        text: `ต้องการลบประเภทงาน: "${job.name}" ใช่หรือไม่?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: formatAdminSwalButton('trash', this.$t("mgmt.delete") || "ลบ"),
        cancelButtonText: formatAdminSwalButton('ban', this.$t("mgmt.cancel") || "ยกเลิก"),
      });
      if (!confirm.isConfirmed) return;

      const subCategories = (sec.subCategories || []).filter(
        (s) => (s._id ? s._id !== job._id : s.name !== job.name)
      );

      // Normalize department IDs
      subCategories.forEach((s) => {
        if (s.department && typeof s.department === "object" && s.department._id) {
          s.department = s.department._id;
        }
      });

      try {
        await axios.put(`${process.env.VUE_APP_APP_URL}/category`, {
          _id: sec._id,
          departmentName: sec.departmentName,
          code: sec.code,
          subCategories,
        });
        Swal.fire({ icon: "success", title: this.$t("jobList.deleteJobSuccess"), toast: true, position: "top-end", showConfirmButton: false, timer: 1500 });
        await this.loadCategories();
      } catch (err) {
        Swal.fire(this.$t("mgmt.error") || "ข้อผิดพลาด", err.response?.data?.message || "ไม่สามารถลบประเภทงานได้", "error");
      }
    },

    // ----------------------------------------------------
    // Icon Upload Operations
    // ----------------------------------------------------
    triggerJobIconUpload(sec, job) {
      this.currentUploadingSec = sec;
      this.currentUploadingJob = job;
      this.showIconAttachment = true;
    },

    async handleJobIconUpload(event) {
      const file = event.target.files[0];
      if (!file || this.iconUploading || !this.currentUploadingSec || !this.currentUploadingJob) return;
      this.iconUploading = true;

      const formData = new FormData();
      formData.append("image", file);

      try {
        const res = await axios.post(`${process.env.VUE_APP_APP_URL}/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.data.success) {
          const sec = this.currentUploadingSec;
          const job = this.currentUploadingJob;
          const subCategories = JSON.parse(JSON.stringify(sec.subCategories || []));

          subCategories.forEach((s) => {
            if (s.department && typeof s.department === "object" && s.department._id) {
              s.department = s.department._id;
            }
            if (s._id === job._id || s.name === job.name) {
              s.icon = res.data.path;
            }
          });

          await axios.put(`${process.env.VUE_APP_APP_URL}/category`, {
            _id: sec._id,
            departmentName: sec.departmentName,
            code: sec.code,
            subCategories,
          });

          job.icon = res.data.path;
          Swal.fire({ icon: "success", title: this.$t("jobTypeMgmt.uploadToast") || "อัปโหลดรูปไอคอนสำเร็จ", toast: true, position: "top-end", showConfirmButton: false, timer: 1500 });
        }
      } catch (err) {
        console.error("Upload Error:", err);
        Swal.fire(this.$t("mgmt.error") || "ข้อผิดพลาด", this.$t("jobTypeMgmt.uploadErr") || "อัปโหลดรูปภาพล้มเหลว", "error");
      } finally {
        this.iconUploading = false;
        this.showIconAttachment = false;
        this.currentUploadingSec = null;
        this.currentUploadingJob = null;
      }
    },

    async handleRemoveJobIcon() {
      if (this.iconUploading || !this.currentUploadingSec || !this.currentUploadingJob) return;
      this.iconUploading = true;
      try {
        const sec = this.currentUploadingSec;
        const job = this.currentUploadingJob;
        const subCategories = JSON.parse(JSON.stringify(sec.subCategories || []));

        subCategories.forEach((s) => {
          if (s.department && typeof s.department === "object" && s.department._id) {
            s.department = s.department._id;
          }
          if (s._id === job._id || s.name === job.name) {
            s.icon = null;
          }
        });

        await axios.put(`${process.env.VUE_APP_APP_URL}/category`, {
          _id: sec._id,
          departmentName: sec.departmentName,
          code: sec.code,
          subCategories,
        });

        job.icon = null;
        Swal.fire({
          icon: "success",
          title: this.$t("common.deleteSuccess") || "ลบรูปไอคอนสำเร็จ",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (err) {
        console.error("Remove Icon Error:", err);
        Swal.fire(this.$t("mgmt.error") || "ข้อผิดพลาด", "ไม่สามารถลบรูปไอคอนได้", "error");
      } finally {
        this.iconUploading = false;
        this.showIconAttachment = false;
        this.currentUploadingSec = null;
        this.currentUploadingJob = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/staff-management";

.hierarchical-management-page {
  margin-top: 8px;
}

.rounded-lg {
  border-radius: 10px !important;
}

/* Header & Top Bar */
.header-main-icon {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #ffffff;
}

.header-sub-text {
  color: #ffffff !important;
  font-size: 0.88rem;
  font-weight: 500;
  opacity: 0.95;
}

.btn-top-add-section {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #ffffff;
  color: #1e40af;
  border: 1px solid rgba(255, 255, 255, 0.9);
  font-weight: 700;
  font-size: 0.9rem;
  padding: 9px 18px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(0);
  }
}

.control-bar-surface {
  background-color: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02) !important;
}

.search-input-group {
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }

  input {
    box-shadow: none;
    border: none;
    font-size: 0.9rem;
    padding: 9px 14px;
    &:focus {
      outline: none;
      box-shadow: none;
    }
  }
}

.badge-stat {
  font-size: 0.825rem;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 8px;

  &--section {
    background: #eff6ff;
    color: #1e40af;
    border: 1px solid #dbeafe;
  }
  &--dept {
    background: #f8fafc;
    color: #475569;
    border: 1px solid #e2e8f0;
  }
}

/* Sections Container Spacing */
.sections-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Group Banners & Dividers */
.sections-group-banner {
  background: #eff6ff;
  border: 1px solid #bfdbfe;

  .badge-group-pill {
    font-size: 0.8rem;
    border-radius: 6px;
    background-color: #16a34a !important;
    color: #ffffff !important;
  }

  .group-title-text {
    font-size: 0.95rem;
    color: #1e3a8a;
  }
}

.sections-group-divider {
  position: relative;
}

.divider-line {
  height: 2px;
  background: linear-gradient(90deg, #cbd5e1 0%, #94a3b8 50%, #cbd5e1 100%);
  border-radius: 2px;
}

.divider-chip {
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  white-space: nowrap;
}

/* Empty Section styling */
.section-box--empty {
  border-color: #e2e8f0 !important;
  opacity: 0.94;

  .section-header--empty {
    background: #475569 !important;

    &:hover {
      background: #334155 !important;
    }
  }

  .section-badge-icon--empty {
    background: #f1f5f9;
    color: #64748b;
  }
}

.badge-empty-tag {
  background: rgba(255, 255, 255, 0.16);
  color: #f1f5f9;
  border: 1px solid rgba(255, 255, 255, 0.28);
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
}

/* Section Big Box */
.section-big-box {
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03) !important;
  transition: all 0.2s ease;
  overflow: hidden;
  margin-bottom: 0 !important;

  &.section-box--has-data {
    border-color: #bfdbfe !important;
  }

  &:hover {
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05) !important;
  }
}

.section-header {
  background: #1e40af !important;
  user-select: none;
  padding: 16px 20px !important;
  transition: background 0.2s ease;

  &:hover {
    background: #1d4ed8 !important;
  }
}

.section-badge-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #ffffff;
  color: #1e40af;
  flex-shrink: 0;
  margin-right: 16px;
}

.section-title-text {
  font-size: 1.125rem;
  letter-spacing: 0.1px;
  color: #ffffff !important;
}

.section-meta-text {
  color: #e2e8f0 !important;
  font-size: 0.84rem;
  font-weight: 500;

  span, strong {
    color: #e2e8f0 !important;
  }
}

.badge-code {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  &--section {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.3);
  }

  &--dept {
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
  }
}

.badge-active-tag {
  background: #dcfce7;
  color: #15803d;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
}

/* Action Buttons (Consistent Minimal Design) */
.btn-admin-action {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  font-size: 0.825rem;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    background: #f8fafc;
    border-color: #94a3b8;
  }
}

.btn-section-action {
  border-radius: 8px;
  font-size: 0.825rem;
  font-weight: 600;
  padding: 6px 14px;
  min-height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;

  &--add-dept {
    background: #ffffff;
    color: #1e40af;
    border: 1px solid #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    &:hover {
      background: #f0f9ff;
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
    }
  }

  &--edit-sec {
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.28);
    &:hover {
      background: rgba(255, 255, 255, 0.22);
      border-color: rgba(255, 255, 255, 0.4);
    }
  }

  &--delete-sec {
    background: rgba(220, 38, 38, 0.18);
    color: #fee2e2;
    border: 1px solid rgba(248, 113, 113, 0.35);
    &:hover {
      background: #dc2626;
      color: #ffffff;
      border-color: #dc2626;
    }
  }

  &--toggle {
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.28);
    &:hover {
      background: rgba(255, 255, 255, 0.22);
      border-color: rgba(255, 255, 255, 0.4);
    }
  }
}

.btn-dept-action {
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 5px 12px;
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;

  &--add-job,
  &--add-central {
    background: #0284c7;
    color: #ffffff;
    border: 1px solid #0284c7;
    box-shadow: 0 1px 3px rgba(2, 132, 199, 0.15);
    &:hover {
      background: #0369a1;
      border-color: #0369a1;
      transform: translateY(-1px);
    }
  }

  &--edit-dept {
    background: #ffffff;
    color: #334155;
    border: 1px solid #cbd5e1;
    &:hover {
      background: #f8fafc;
      border-color: #94a3b8;
      color: #1e293b;
    }
  }

  &--delete-dept {
    background: #fee2e2;
    color: #dc2626;
    border: 1px solid #fca5a5;
    &:hover {
      background: #dc2626;
      color: #ffffff;
      border-color: #dc2626;
    }
  }
}

.section-body {
  padding: 20px !important;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Department Long Box */
.department-long-box {
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  background-color: #ffffff;
  transition: all 0.2s ease;
  overflow: hidden;
  margin-bottom: 0 !important;

  &:hover {
    border-color: #cbd5e1 !important;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.04) !important;
  }

  &--central {
    border-style: dashed !important;
    border-color: #93c5fd !important;
  }
}

.dept-header {
  background: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 12px 18px !important;

  &--central {
    background: #f0f9ff !important;
    border-bottom: 1px solid #e0f2fe !important;
  }
}

.dept-name-title {
  font-size: 0.95rem;
  color: #1e293b;
}

.badge-status-pill {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;

  &--active {
    background: #dcfce7;
    color: #15803d;
    border: 1px solid #bbf7d0;
  }

  &--inactive {
    background: #f1f5f9;
    color: #64748b;
    border: 1px solid #e2e8f0;
  }
}

.badge-count-pill {
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #dbeafe;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 6px;
}

/* Job Table Inside Department */
.job-nested-table {
  border-collapse: separate;
  border-spacing: 0;
}

.job-table-thead {
  background: #fafafa;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-bottom: 1px solid #e2e8f0;

  th {
    padding: 10px 14px;
    border-top: none;
    border-bottom: 1px solid #e2e8f0;
  }
}

.job-item-row {
  transition: background-color 0.15s ease;

  td {
    padding: 10px 14px;
    border-top: 1px solid #f1f5f9;
  }

  &:hover {
    background-color: #f8fafc !important;
  }
}

.job-title-text {
  font-size: 0.9rem;
  color: #1e293b;
}

.badge-sla-tag {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
}

.job-icon-box {
  width: 36px;
  height: 36px;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #ffffff;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    background-color: #eff6ff;
  }
}

.job-icon-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 2px;
  border-radius: 6px;
}

.job-icon-svg {
  width: 20px;
  height: 20px;
  color: #2563eb;
  transition: transform 0.2s ease, color 0.2s ease;
}

.job-icon-box:hover .job-icon-svg {
  transform: scale(1.15);
  color: #1d4ed8;
}

.job-icon-placeholder {
  font-size: 1rem;
  line-height: 1;
  user-select: none;
}

.btn-table-action {
  min-width: auto;
  height: 30px;
  padding: 3px 9px;
  border-radius: 8px;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s ease;

  @media (max-width: 1100px) {
    padding: 3px 7px;
    span {
      display: none;
    }
  }

  &--edit {
    background: #ffffff;
    color: #1e40af;
    border: 1px solid rgba(37, 99, 235, 0.25);
    &:hover {
      background: #f0f9ff;
      border-color: #1e40af;
    }
  }

  &--delete {
    background: #ffffff;
    color: #dc2626;
    border: 1px solid #fca5a5;
    &:hover {
      background: #dc2626;
      color: #ffffff;
      border-color: #dc2626;
    }
  }
}

::v-deep .admin-action-btn,
::v-deep .admin-close-btn {
  border-radius: 8px !important;
}

@media (max-width: 1150px) {
  .section-header {
    gap: 12px;
  }
  .section-actions-wrapper {
    width: 100%;
    justify-content: flex-start !important;
    margin-top: 10px !important;
  }
  .dept-header {
    gap: 10px;
  }
  .dept-actions-wrapper {
    width: 100%;
    justify-content: flex-start !important;
    margin-top: 8px !important;
  }
}

@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 12px;
    padding: 14px 16px !important;
  }
  .section-actions-wrapper {
    display: grid !important;
    grid-template-columns: repeat(2, 1fr) !important;
    width: 100% !important;
    gap: 8px !important;
    margin-top: 10px !important;
  }
  .section-actions-wrapper .btn-section-action {
    width: 100% !important;
    padding: 6px 8px !important;
    font-size: 0.8rem !important;
  }
  .dept-header {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 10px;
    padding: 12px 14px !important;
  }
  .dept-actions-wrapper {
    display: flex !important;
    flex-wrap: wrap !important;
    width: 100% !important;
    gap: 6px !important;
    margin-top: 8px !important;
  }
  .dept-actions-wrapper .btn-dept-action {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    padding: 5px 8px !important;
    font-size: 0.78rem !important;
  }
}
</style>